import React, { useState, useEffect, useRef } from "react";
import Modal from "./Modal.tsx"; // Adjust path as needed

interface SharedFilters {
    searchTerm: string;
    classificationFilter: string;
    domainFilter: string;
    ukBiobankFilter: string;
    polymorphicFilter: string;
}

interface Props {
    targetName: string;
    data: Record<string, any>;
    sharedFilters?: SharedFilters;
    setSharedFilters?: (filters: SharedFilters) => void;
    isGPCR?: boolean;
    gnomadRows?: any[]; // Pre-parsed gnomAD rows shared from the page
    domains?: any[]; // Protein domain annotations shared from the page
}

function RSIDTABLE({ targetName, data, sharedFilters, setSharedFilters, isGPCR = false, gnomadRows, domains }: Props) {
    console.log(data)
    const [expandedRows, setExpandedRows] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [mutationsPerRow] = useState(5); // Fixed at 5
    const [domainData, setDomainData] = useState([]);
    const [validatedMutationsData, setValidatedMutationsData] = useState<Map<number, any[]>>(new Map());
    const [polymorphicRsids, setPolymorphicRsids] = useState<Map<string, number>>(new Map());

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRsid, setSelectedRsid] = useState("");
    const [selectedGene, setSelectedGene] = useState("");

    // Experimental validation modal state
    const [evModalOpen, setEvModalOpen] = useState(false);
    const [selectedEvData, setSelectedEvData] = useState<any[]>([]);

    // Sorting state
    const [sortBy, setSortBy] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    // Column visibility — let users choose which data columns to display
    const [visibleColumns, setVisibleColumns] = useState({
        mutations: true,
        positionClassification: true,
        alphaMissense: true,
        biobank: true,
        polymorphic: true,
        experimentalValidation: true,
    });
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const columnsMenuRef = useRef<HTMLDivElement>(null);

    const COLUMN_OPTIONS: { key: keyof typeof visibleColumns; label: string }[] = [
        { key: "mutations", label: "Mutations" },
        { key: "positionClassification", label: "Position Classification" },
        { key: "alphaMissense", label: "AlphaMissense Prediction" },
        { key: "biobank", label: "Biobank" },
        { key: "polymorphic", label: "Polymorphic" },
        ...(isGPCR ? [{ key: "experimentalValidation" as const, label: "Experimental Validation" }] : []),
    ];

    const toggleColumn = (key: keyof typeof visibleColumns) => {
        setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Close the columns menu when clicking outside it
    useEffect(() => {
        if (!columnsMenuOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target as Node)) {
                setColumnsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [columnsMenuOpen]);

    // Use shared filters if provided, otherwise use local state
    const searchTerm = sharedFilters?.searchTerm ?? "";
    const classificationFilter = sharedFilters?.classificationFilter ?? "all";
    const domainFilter = sharedFilters?.domainFilter ?? "all";
    const ukBiobankFilter = sharedFilters?.ukBiobankFilter ?? "all";
    const polymorphicFilter = sharedFilters?.polymorphicFilter ?? "all";

    const updateSharedFilter = (key: keyof SharedFilters, value: string) => {
        if (setSharedFilters && sharedFilters) {
            setSharedFilters({ ...sharedFilters, [key]: value });
        }
    };

    // Use domains shared from the page if provided; otherwise fetch them.
    useEffect(() => {
        if (domains) {
            setDomainData(domains as any);
            return;
        }
        const fetchDomainData = async () => {
            try {
                const response = await fetch('/api/domains');
                if (response.ok) {
                    const data = await response.json();
                    setDomainData(data);
                }
            } catch (error) {
                console.error('Error fetching domain data:', error);
            }
        };

        fetchDomainData();
    }, [domains]);

    // Fetch validated mutations data (only for GPCR targets)
    useEffect(() => {
        const fetchValidatedMutations = async () => {
            if (!targetName || !isGPCR) {
                setValidatedMutationsData(new Map());
                return;
            }

            try {
                const response = await fetch(`/api/getData?type=validatedMutations&gene_name=${encodeURIComponent(targetName)}`);
                if (response.ok) {
                    const data = await response.json();
                    // Create a map of position -> array of validation data
                    const positionMap = new Map<number, any[]>();
                    (data.mutations || []).forEach((m: any) => {
                        if (m.position && m.experimental_validation === 'yes') {
                            const pos = Number(m.position);
                            if (!positionMap.has(pos)) {
                                positionMap.set(pos, []);
                            }
                            positionMap.get(pos)!.push(m);
                        }
                    });
                    setValidatedMutationsData(positionMap);
                }
            } catch (error) {
                console.error('Error fetching validated mutations:', error);
            }
        };

        fetchValidatedMutations();
    }, [targetName, isGPCR]);

    // Build a map of rsID -> max population MAF (an RSID is "polymorphic" if any
    // population has frequency > 0.01 / 1%). Uses rows shared from the page if
    // provided, otherwise fetches this gene's frequency rows from the DB.
    useEffect(() => {
        const buildMap = (rowsArr: any[]) => {
            const maxFreq = new Map<string, number>();
            rowsArr.forEach(r => {
                const rsid = String(r.rsID ?? '').trim();
                if (!rsid) return;
                const f = typeof r.Frequency === 'number' ? r.Frequency : parseFloat(r.Frequency) || 0;
                const prev = maxFreq.get(rsid) ?? 0;
                if (f > prev) maxFreq.set(rsid, f);
            });
            setPolymorphicRsids(maxFreq);
        };

        if (gnomadRows) {
            buildMap(gnomadRows);
            return;
        }

        if (!targetName) return;
        const loadPolymorphicData = async () => {
            try {
                const response = await fetch(`/api/getData?type=frequency&gene=${encodeURIComponent(targetName)}`);
                if (!response.ok) return;
                const json = await response.json();
                buildMap(Array.isArray(json.rows) ? json.rows : []);
            } catch (e) {
                console.error('Error loading polymorphic data:', e);
            }
        };
        loadPolymorphicData();
    }, [gnomadRows, targetName]);

    const getPositionClassification = (position: number, uniprotId: string) => {
        if (!domainData.length || !position) return {
            type: "Unknown",
            style: { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' },
            featureType: "No Data"
        };

        // Find domains for this UniProt ID
        const proteinDomains = domainData.filter(domain =>
            domain.uniprot_id === uniprotId
        );

        // Check if position falls within any domain
        for (const domain of proteinDomains) {
            if (position >= domain.start_position && position <= domain.end_position) {
                if (domain.feature_type === "Transmembrane") {
                    return {
                        type: domain.description || "Transmembrane",
                        style: { backgroundColor: '#1A2940', color: 'white', border: '1px solid #1A2940' },
                        featureType: "Transmembrane"
                    };
                } else if (domain.feature_type === "Topological domain") {
                    return {
                        type: domain.description || "Topological",
                        style: { backgroundColor: '#e5e7eb', color: '#1A2940', border: '1px solid #d1d5db' },
                        featureType: "Topological domain"
                    };
                }
            }
        }

        return {
            type: "Extracellular/Other",
            style: { backgroundColor: '#f9fafb', color: '#4b5563', border: '1px solid #d1d5db' },
            featureType: "Outside domains"
        };
    };

    const extractPositionFromMutation = (mutation: string) => {
        // Extract position from mutation string like "A123T" or "p.Ala123Thr"
        const match = mutation.match(/(\d+)/);
        return match ? parseInt(match[1]) : null;
    };

    // Check if a mutation position has experimental validation
    const hasExperimentalValidation = (mutation: string): boolean => {
        const position = extractPositionFromMutation(mutation);
        if (position === null) return false;
        return validatedMutationsData.has(position);
    };

    // Get experimental validation data for a mutation position
    const getExperimentalValidationData = (mutation: string): any[] => {
        const position = extractPositionFromMutation(mutation);
        if (position === null) return [];
        return validatedMutationsData.get(position) || [];
    };


    // Escape a value for safe inclusion in a CSV cell
    const escapeCSVValue = (value: any): string => {
        if (value === null || value === undefined) return "";
        const stringValue = String(value);
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    // Export the currently filtered/sorted table to CSV, respecting visible columns
    // and the different table "shapes" (GPCR targets have an extra validation column,
    // and RSIDs can have zero, one, or many mutations each).
    const exportToCSV = () => {
        const headers = ["#", "RSID"];
        if (visibleColumns.mutations) headers.push("Mutation");
        if (visibleColumns.positionClassification) headers.push("Position Classification");
        if (visibleColumns.alphaMissense) headers.push("AlphaMissense Classification");
        if (visibleColumns.biobank) headers.push("Biobank");
        if (visibleColumns.polymorphic) headers.push("Polymorphic");
        if (isGPCR && visibleColumns.experimentalValidation) headers.push("Experimental Validation");

        const rows: string[][] = [];

        sortedRows.forEach((rsID, rsidIndex) => {
            const mutationsList = mutations[rsID] || [];
            const ukBiobank = data[0]?.rsids?.[rsID]?.uk_biobank;
            const biobankLabel = ukBiobank ? (ukBiobank === "yes" ? "Found" : "Not Found") : "No Data";
            const maxFreq = polymorphicRsids.get(rsID);
            const polymorphicLabel = maxFreq === undefined ? "No Data" : (maxFreq > 0.01 ? "Yes" : "No");

            // RSIDs with no mutations still get a row with the RSID-level data
            if (mutationsList.length === 0) {
                const row: string[] = [String(rsidIndex + 1), rsID];
                if (visibleColumns.mutations) row.push("");
                if (visibleColumns.positionClassification) row.push("");
                if (visibleColumns.alphaMissense) row.push("");
                if (visibleColumns.biobank) row.push(biobankLabel);
                if (visibleColumns.polymorphic) row.push(polymorphicLabel);
                if (isGPCR && visibleColumns.experimentalValidation) row.push("");
                rows.push(row);
                return;
            }

            mutationsList.forEach((mutationObj: any, mIndex: number) => {
                const mutation = mutationObj.mutation;
                const position = extractPositionFromMutation(mutation);
                const uniprotId = mutationObj.prediction?.uniprot_id || data[0]?.uniprot_id;
                const classification = getPositionClassification(position, uniprotId);
                const pred = mutationObj.prediction || {};
                const hasPrediction = pred.position !== undefined && pred.original_aa && pred.mutated_aa && pred.classification;
                const isValidated = isGPCR && hasExperimentalValidation(mutation);

                // RSID-level columns (biobank, polymorphic) only print on the first mutation row
                const row: string[] = [
                    mIndex === 0 ? String(rsidIndex + 1) : "",
                    mIndex === 0 ? rsID : "",
                ];
                if (visibleColumns.mutations) row.push(mutation || "");
                if (visibleColumns.positionClassification) row.push(classification.type);
                if (visibleColumns.alphaMissense) row.push(hasPrediction ? pred.classification : "No Available Data");
                if (visibleColumns.biobank) row.push(mIndex === 0 ? biobankLabel : "");
                if (visibleColumns.polymorphic) row.push(mIndex === 0 ? polymorphicLabel : "");
                if (isGPCR && visibleColumns.experimentalValidation) row.push(isValidated ? "Yes" : "No");

                rows.push(row);
            });
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(escapeCSVValue).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const safeTargetName = (targetName || "export").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `${safeTargetName}_mutations.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const toggleRow = (rsID: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rsID]: !prev[rsID],
        }));
    };

    // Modal functions
    const openModal = (rsid: string, gene: string) => {
        setSelectedRsid(rsid);
        setSelectedGene(gene);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedRsid("");
        setSelectedGene("");
    };

    const mutations = data[0]?.mutations || {};

    // Apply filters
    const filteredRSIDs = Object.keys(mutations).filter(rsID => {
        const mutationsList = mutations[rsID] || [];

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesRSID = rsID.toLowerCase().includes(searchLower);
            const matchesMutation = mutationsList.some(m =>
                m.mutation?.toLowerCase().includes(searchLower)
            );
            if (!matchesRSID && !matchesMutation) return false;
        }

        // Classification filter
        if (classificationFilter !== "all") {
            const hasMatchingClassification = mutationsList.some(m =>
                m.prediction?.classification?.toLowerCase() === classificationFilter.toLowerCase()
            );
            if (!hasMatchingClassification) return false;
        }

        // Domain filter
        if (domainFilter !== "all") {
            const hasMatchingDomain = mutationsList.some(m => {
                const mutation = m.mutation;
                const position = extractPositionFromMutation(mutation);
                const uniprotId = m.prediction?.uniprot_id || data[0]?.uniprot_id;
                const classification = getPositionClassification(position, uniprotId);
                return classification.featureType === domainFilter;
            });
            if (!hasMatchingDomain) return false;
        }

        // Biobank filter
        if (ukBiobankFilter !== "all") {
            const ukBiobank = data[0]?.rsids?.[rsID]?.uk_biobank;
            if (ukBiobankFilter === "yes" && ukBiobank !== "yes") return false;
            if (ukBiobankFilter === "no" && ukBiobank === "yes") return false;
        }

        // Polymorphic filter (gnomAD MAF > 1% in any population)
        if (polymorphicFilter !== "all") {
            const maxFreq = polymorphicRsids.get(rsID);
            if (polymorphicFilter === "nodata") {
                if (maxFreq !== undefined) return false;
            } else if (polymorphicFilter === "yes") {
                if (maxFreq === undefined || maxFreq <= 0.01) return false;
            } else if (polymorphicFilter === "no") {
                if (maxFreq === undefined || maxFreq > 0.01) return false;
            }
        }

        return true;
    });

    // Handle sorting
    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(column);
            setSortOrder("asc");
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Sort the filtered mutation rows
    const sortedRows = filteredRSIDs.sort((a, b) => {
        if (!sortBy) return 0;

        if (sortBy === "position") {
            // Get the first mutation's position for each RSID
            const getFirstPosition = (rsID: string) => {
                const mutationsList = mutations[rsID] || [];
                if (mutationsList.length === 0) return Infinity;
                const mutation = mutationsList[0]?.mutation || "";
                return extractPositionFromMutation(mutation) || Infinity;
            };

            const posA = getFirstPosition(a);
            const posB = getFirstPosition(b);
            return sortOrder === "asc" ? posA - posB : posB - posA;
        }

        if (sortBy === "classification") {
            // Get the first mutation's classification
            const getFirstClassification = (rsID: string) => {
                const mutationsList = mutations[rsID] || [];
                if (mutationsList.length === 0) return "";
                return mutationsList[0]?.prediction?.classification || "";
            };

            const classA = getFirstClassification(a);
            const classB = getFirstClassification(b);
            const comparison = classA.localeCompare(classB);
            return sortOrder === "asc" ? comparison : -comparison;
        }

        if (sortBy === "polymorphic") {
            // Sort by max population MAF; treat "no data" as -1 so it sinks on asc.
            const freqA = polymorphicRsids.get(a) ?? -1;
            const freqB = polymorphicRsids.get(b) ?? -1;
            return sortOrder === "asc" ? freqA - freqB : freqB - freqA;
        }

        return 0;
    });

    const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
    const paginatedRows = sortedRows.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    return (
        <div className="mt-4">
            {/* Filters and Controls */}
            <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3" style={{ rowGap: '0.5rem' }}>
                    {/* Search */}
                    <div style={{ flex: '1 1 140px', minWidth: '120px', maxWidth: '240px' }}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => {
                                updateSharedFilter('searchTerm', e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                fontSize: '0.8125rem',
                                borderColor: '#d1d5db',
                                padding: '0.375rem 0.625rem'
                            }}
                        />
                    </div>

                    {/* Classification Filter */}
                    <select
                        value={classificationFilter}
                        onChange={(e) => {
                            updateSharedFilter('classificationFilter', e.target.value);
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{
                            width: 'auto',
                            fontSize: '0.8125rem',
                            borderColor: '#d1d5db'
                        }}
                    >
                        <option value="all">All Predictions</option>
                        <option value="pathogenic">Pathogenic</option>
                        <option value="benign">Benign</option>
                        <option value="ambiguous">Ambiguous</option>
                    </select>

                    {/* Domain Filter */}
                    <select
                        value={domainFilter}
                        onChange={(e) => {
                            updateSharedFilter('domainFilter', e.target.value);
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{
                            width: 'auto',
                            fontSize: '0.8125rem',
                            borderColor: '#d1d5db'
                        }}
                    >
                        <option value="all">All Domains</option>
                        <option value="Transmembrane">Transmembrane</option>
                        <option value="Topological domain">Topological</option>
                        <option value="Outside domains">Outside</option>
                    </select>

                    {/* Biobank Filter */}
                    <select
                        value={ukBiobankFilter}
                        onChange={(e) => {
                            updateSharedFilter('ukBiobankFilter', e.target.value);
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{
                            width: 'auto',
                            fontSize: '0.8125rem',
                            borderColor: '#d1d5db'
                        }}
                    >
                        <option value="all">Biobank: All</option>
                        <option value="yes">Biobank: Found</option>
                        <option value="no">Biobank: Not Found</option>
                    </select>

                    {/* Polymorphic Filter */}
                    <select
                        value={polymorphicFilter}
                        onChange={(e) => {
                            updateSharedFilter('polymorphicFilter', e.target.value);
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{
                            width: 'auto',
                            fontSize: '0.8125rem',
                            borderColor: '#d1d5db'
                        }}
                    >
                        <option value="all">Polymorphic: All</option>
                        <option value="yes">Polymorphic: Yes</option>
                        <option value="no">Polymorphic: No</option>
                        <option value="nodata">Polymorphic: No Data</option>
                    </select>

                    {/* Display Controls */}
                    <select
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                        className="form-select form-select-sm"
                        style={{
                            width: 'auto',
                            fontSize: '0.8125rem',
                            borderColor: '#d1d5db'
                        }}
                    >
                        <option value={5}>5 rows</option>
                        <option value={10}>10 rows</option>
                        <option value={15}>15 rows</option>
                        <option value={20}>20 rows</option>
                        <option value={30}>30 rows</option>
                        <option value={50}>50 rows</option>
                    </select>

                    {/* Column visibility dropdown */}
                    <div ref={columnsMenuRef} style={{ position: 'relative' }}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setColumnsMenuOpen(o => !o)}
                            style={{
                                fontSize: '0.8125rem',
                                border: '1px solid #d1d5db',
                                backgroundColor: columnsMenuOpen ? '#1A2940' : '#fff',
                                color: columnsMenuOpen ? '#fff' : '#1A2940',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '0.375rem 0.625rem',
                                fontWeight: 500,
                            }}
                            title="Choose which columns to display"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                            </svg>
                            Columns
                        </button>
                        {columnsMenuOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1050,
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 6px 20px rgba(15,23,42,0.15)',
                                    padding: '0.5rem',
                                    minWidth: '220px',
                                }}
                            >
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0.25rem 0.5rem 0.4rem' }}>
                                    Show columns
                                </div>
                                {COLUMN_OPTIONS.map(col => (
                                    <label
                                        key={col.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '0.35rem 0.5rem',
                                            fontSize: '0.8125rem',
                                            color: '#1A2940',
                                            cursor: 'pointer',
                                            borderRadius: '5px',
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={visibleColumns[col.key]}
                                            onChange={() => toggleColumn(col.key)}
                                            style={{ accentColor: '#1A2940', width: '15px', height: '15px', cursor: 'pointer' }}
                                        />
                                        {col.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Export to CSV */}
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={exportToCSV}
                        style={{
                            fontSize: '0.8125rem',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#fff',
                            color: '#1A2940',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '0.375rem 0.625rem',
                            fontWeight: 500,
                        }}
                        title="Export the current table view as a CSV file"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Export CSV
                    </button>

                    {/* Result Counter */}
                    <div
                        className="ms-auto"
                        style={{
                            color: '#1A2940',
                            fontSize: '0.8125rem',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {sortedRows.length} / {Object.keys(mutations).length}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <table className="table table-borderless table-striped table-hover">
                    <thead className="text-center">
                        <tr style={{ backgroundColor: '#1A2940', color: 'white' }}>
                            <th scope="col" style={{ backgroundColor: '#1A2940', color: 'white' }}>#</th>
                            <th scope="col" style={{ backgroundColor: '#1A2940', color: 'white' }}>
                                RSID
                            </th>
                            {visibleColumns.mutations && (
                            <th
                                scope="col"
                                onClick={() => handleSort("position")}
                                style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    backgroundColor: '#1A2940',
                                    color: 'white',
                                    position: 'relative',
                                    paddingRight: '1.5rem'
                                }}
                            >
                                Mutations
                                <span style={{
                                    position: 'absolute',
                                    right: '0.5rem',
                                    opacity: sortBy === "position" ? 1 : 0.4,
                                    fontSize: '0.8rem'
                                }}>
                                    {sortBy === "position" && sortOrder === "asc" ? "▲" : sortBy === "position" && sortOrder === "desc" ? "▼" : "↕"}
                                </span>
                            </th>
                            )}
                            {visibleColumns.positionClassification && (
                            <th scope="col" style={{ backgroundColor: '#1A2940', color: 'white' }}>Position Classification</th>
                            )}
                            {visibleColumns.alphaMissense && (
                            <th
                                scope="col"
                                onClick={() => handleSort("classification")}
                                style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    backgroundColor: '#1A2940',
                                    color: 'white',
                                    position: 'relative',
                                    paddingRight: '1.5rem'
                                }}
                            >
                                AlphaMissense Prediction
                                <span style={{
                                    position: 'absolute',
                                    right: '0.5rem',
                                    opacity: sortBy === "classification" ? 1 : 0.4,
                                    fontSize: '0.8rem'
                                }}>
                                    {sortBy === "classification" && sortOrder === "asc" ? "▲" : sortBy === "classification" && sortOrder === "desc" ? "▼" : "↕"}
                                </span>
                            </th>
                            )}
                            {visibleColumns.biobank && (
                            <th scope="col" style={{ backgroundColor: '#1A2940', color: 'white' }}>Biobank</th>
                            )}
                            {visibleColumns.polymorphic && (
                            <th
                                scope="col"
                                onClick={() => handleSort("polymorphic")}
                                style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    backgroundColor: '#1A2940',
                                    color: 'white',
                                    position: 'relative',
                                    paddingRight: '1.5rem'
                                }}
                            >
                                Polymorphic
                                <span style={{
                                    position: 'absolute',
                                    right: '0.5rem',
                                    opacity: sortBy === "polymorphic" ? 1 : 0.4,
                                    fontSize: '0.8rem'
                                }}>
                                    {sortBy === "polymorphic" && sortOrder === "asc" ? "▲" : sortBy === "polymorphic" && sortOrder === "desc" ? "▼" : "↕"}
                                </span>
                            </th>
                            )}
                            {isGPCR && visibleColumns.experimentalValidation && (
                                <th scope="col" style={{ backgroundColor: '#1A2940', color: 'white' }}>Experimental Validation</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="text-center">
                        {paginatedRows.map((rsID, index) => {
                            const mutationsList = mutations[rsID] || [];

                            const isExpanded = expandedRows[rsID];
                            const visibleMutations = isExpanded ? mutationsList : mutationsList.slice(0, mutationsPerRow);

                            // Check if any mutation in this row has experimental validation
                            const rowHasValidation = isGPCR && mutationsList.some(m => hasExperimentalValidation(m.mutation));

                            // Collect all validation data for this row
                            const getRowValidationData = () => {
                                const allData: any[] = [];
                                mutationsList.forEach(m => {
                                    const data = getExperimentalValidationData(m.mutation);
                                    allData.push(...data);
                                });
                                return allData;
                            };

                            return (
                                <tr
                                    key={index}
                                    onClick={() => {
                                        if (rowHasValidation) {
                                            setSelectedEvData(getRowValidationData());
                                            setEvModalOpen(true);
                                        }
                                    }}
                                    style={{
                                        cursor: rowHasValidation ? 'pointer' : 'default',
                                        backgroundColor: rowHasValidation ? '#f0f9ff' : 'inherit',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (rowHasValidation) {
                                            e.currentTarget.style.backgroundColor = '#dbeafe';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (rowHasValidation) {
                                            e.currentTarget.style.backgroundColor = '#f0f9ff';
                                        }
                                    }}
                                    title={rowHasValidation ? 'Click to view experimental validation data' : ''}
                                >
                                    <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                    
                                    {/* Clickable RSID */}
                                    <td>
                                        <button
                                            className="btn btn-link p-0"
                                            style={{ textDecoration: "none", color: "#1A2940", fontWeight: "bold" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openModal(rsID, targetName);
                                            }}
                                            title="Click to view frequency map"
                                        >
                                            {rsID}
                                        </button>
                                    </td>

                                    {/* Mutations Column */}
                                    {visibleColumns.mutations && (
                                    <td>
                                        {visibleMutations.map((mutationObj, i) => {
                                            const mutation = mutationObj.mutation;
                                            const ensemblId = mutationObj.prediction?.ensemble_id ?? "Unknown";

                                            return (
                                                <div key={i} className="mb-2 text-center">
                                                    {mutation}
                                                    <span
                                                        title={`Ensembl Transcript ID: ${ensemblId}`}
                                                        style={{
                                                            display: "inline-block",
                                                            border: "1px solid #1A2940",
                                                            borderRadius: "50%",
                                                            width: "16px",
                                                            height: "16px",
                                                            lineHeight: "14px",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            color: "#1A2940",
                                                            marginLeft: "6px",
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        ?
                                                    </span>
                                                </div>
                                            );
                                        })}

                                        {mutationsList.length > mutationsPerRow && (
                                            <div className="text-center">
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    style={{ textDecoration: "none", color: "#1A2940" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleRow(rsID);
                                                    }}
                                                >
                                                    {isExpanded ? "Show Less" : "Show More"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    )}

                                    {/* Position Classification Column */}
                                    {visibleColumns.positionClassification && (
                                    <td>
                                        {visibleMutations.map((mutationObj, i) => {
                                            const mutation = mutationObj.mutation;
                                            const position = extractPositionFromMutation(mutation);
                                            const uniprotId = mutationObj.prediction?.uniprot_id || data[0]?.uniprot_id;
                                            const classification = getPositionClassification(position, uniprotId);

                                            return (
                                                <div key={i} className="mb-2 text-center">
                                                    <span
                                                        className="badge"
                                                        style={{
                                                            ...classification.style,
                                                            fontSize: '0.75rem',
                                                            padding: '0.25rem 0.5rem'
                                                        }}
                                                    >
                                                        {classification.type}
                                                    </span>
                                                    <div className="small text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                        {classification.featureType || "Unknown"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </td>
                                    )}

                                    {/* AlphaMissense Prediction Column */}
                                    {visibleColumns.alphaMissense && (
                                    <td>
                                        {visibleMutations.map((mutationObj, i) => {
                                            const pred = mutationObj.prediction || {};
                                            const hasPrediction =
                                                pred.position !== undefined &&
                                                pred.original_aa &&
                                                pred.mutated_aa &&
                                                pred.classification;

                                            const getClassificationStyle = (classification: string) => {
                                                switch (classification) {
                                                    case "benign":
                                                        return { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };
                                                    case "pathogenic":
                                                        return { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
                                                    case "ambiguous":
                                                        return { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
                                                    default:
                                                        return { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' };
                                                }
                                            };

                                            return (
                                                <div key={i} className="mb-3 text-center">
                                                    {hasPrediction ? (
                                                        <>
                                                            <div className="small">
                                                                Classification:{" "}
                                                                <span
                                                                    className="badge"
                                                                    style={{
                                                                        ...getClassificationStyle(pred.classification),
                                                                        fontSize: '0.75rem',
                                                                        padding: '0.25rem 0.5rem'
                                                                    }}
                                                                >
                                                                    {pred.classification}
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-muted small fst-italic">No Available Data</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </td>
                                    )}

                                    {/* Biobank Column - show once per RSID */}
                                    {visibleColumns.biobank && (
                                    <td>
                                        {(() => {
                                            const ukBiobank = data[0]?.rsids?.[rsID]?.uk_biobank;

                                            return ukBiobank ? (
                                                <span
                                                    className="badge"
                                                    style={{
                                                        backgroundColor: ukBiobank === "yes" ? '#d1fae5' : '#fee2e2',
                                                        color: ukBiobank === "yes" ? '#065f46' : '#991b1b',
                                                        border: ukBiobank === "yes" ? '1px solid #a7f3d0' : '1px solid #fecaca',
                                                        fontSize: '0.75rem',
                                                        padding: '0.25rem 0.5rem'
                                                    }}
                                                >
                                                    {ukBiobank === "yes" ? "Found" : "Not Found"}
                                                </span>
                                            ) : (
                                                <div className="text-muted small fst-italic">No Data</div>
                                            );
                                        })()}
                                    </td>
                                    )}

                                    {/* Polymorphic Column - based on gnomAD MAF > 1% in any population */}
                                    {visibleColumns.polymorphic && (
                                    <td>
                                        {(() => {
                                            const maxFreq = polymorphicRsids.get(rsID);
                                            if (maxFreq === undefined) {
                                                return <div className="text-muted small fst-italic">No Data</div>;
                                            }
                                            const isPolymorphic = maxFreq > 0.01;
                                            return (
                                                <span
                                                    className="badge"
                                                    title={`Max population MAF: ${(maxFreq * 100).toFixed(3)}%`}
                                                    style={{
                                                        backgroundColor: isPolymorphic ? '#d1fae5' : '#fee2e2',
                                                        color: isPolymorphic ? '#065f46' : '#991b1b',
                                                        border: isPolymorphic ? '1px solid #a7f3d0' : '1px solid #fecaca',
                                                        fontSize: '0.75rem',
                                                        padding: '0.25rem 0.5rem'
                                                    }}
                                                >
                                                    {isPolymorphic ? 'Yes' : 'No'}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    )}

                                    {/* Experimental Validation Column - only for GPCR targets */}
                                    {isGPCR && visibleColumns.experimentalValidation && (
                                        <td>
                                            {visibleMutations.map((mutationObj, i) => {
                                                const mutation = mutationObj.mutation;
                                                const isValidated = hasExperimentalValidation(mutation);

                                                return (
                                                    <div key={i} className="mb-2 text-center">
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: isValidated ? '#dbeafe' : '#f3f4f6',
                                                                color: isValidated ? '#1e40af' : '#6b7280',
                                                                border: isValidated ? '1px solid #93c5fd' : '1px solid #e5e7eb',
                                                                fontSize: '0.75rem',
                                                                padding: '0.25rem 0.5rem'
                                                            }}
                                                        >
                                                            {isValidated ? "Yes" : "No"}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <nav className="d-flex justify-content-center mt-2">
                <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                            className="page-link"
                            style={{
                                backgroundColor: currentPage === 1 ? "#f3f4f6" : "#1A2940",
                                color: currentPage === 1 ? "#9ca3af" : "white",
                                border: "1px solid #e5e7eb",
                                cursor: currentPage === 1 ? "not-allowed" : "pointer"
                            }}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
                        .map(page => (
                            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                                <button
                                    className="page-link"
                                    style={{
                                        backgroundColor: currentPage === page ? "#1A2940" : "#ffffff",
                                        color: currentPage === page ? "white" : "#1A2940",
                                        border: "1px solid #e5e7eb",
                                        fontWeight: currentPage === page ? "600" : "normal"
                                    }}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            </li>
                        ))}

                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                            className="page-link"
                            style={{
                                backgroundColor: currentPage === totalPages ? "#f3f4f6" : "#1A2940",
                                color: currentPage === totalPages ? "#9ca3af" : "white",
                                border: "1px solid #e5e7eb",
                                cursor: currentPage === totalPages ? "not-allowed" : "pointer"
                            }}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>

            {/* Modal */}
            <Modal
                rsid={selectedRsid}
                gene={selectedGene}
                isOpen={modalOpen}
                onClose={closeModal}
            />

            {/* Experimental Validation Modal */}
            {evModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1050
                    }}
                    onClick={() => setEvModalOpen(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            maxWidth: '800px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.25rem 1.5rem',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#1A2940'
                        }}>
                            <h3 style={{ margin: 0, color: 'white', fontSize: '1.125rem', fontWeight: 600 }}>
                                Experimental Validation Data
                            </h3>
                            <button
                                onClick={() => setEvModalOpen(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'white',
                                    lineHeight: 1
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '1.5rem', maxHeight: 'calc(80vh - 70px)', overflowY: 'auto' }}>
                            {selectedEvData.length === 0 ? (
                                <p style={{ color: '#6b7280', textAlign: 'center' }}>No data available</p>
                            ) : (
                                selectedEvData.map((item, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            marginBottom: idx < selectedEvData.length - 1 ? '1.5rem' : 0,
                                            paddingBottom: idx < selectedEvData.length - 1 ? '1.5rem' : 0,
                                            borderBottom: idx < selectedEvData.length - 1 ? '1px solid #e5e7eb' : 'none'
                                        }}
                                    >
                                        {selectedEvData.length > 1 && (
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: '#6b7280',
                                                marginBottom: '0.75rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Entry {idx + 1} of {selectedEvData.length}
                                            </div>
                                        )}

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Mutation</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937', fontFamily: 'monospace' }}>{item.mutation || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Position</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.position || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Ligand</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.ligand || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Complexed Ligand</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.complexed_ligand || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>WT Result</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.wt_result || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Mutant Result</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.mutant_result || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Assay</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.assay || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Cell Line</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.cell_line || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>Units</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.units || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>G-Protein Pathway</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>{item.g_protein_pathway || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>PDB ID</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>
                                                    {item.pdb_id ? (
                                                        <a href={`https://www.rcsb.org/structure/${item.pdb_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                                                            {item.pdb_id}
                                                        </a>
                                                    ) : '-'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>PubMed ID</span>
                                                <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>
                                                    {item.pubmed_id ? (
                                                        <a href={`https://pubmed.ncbi.nlm.nih.gov/${item.pubmed_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                                                            {item.pubmed_id}
                                                        </a>
                                                    ) : '-'}
                                                </span>
                                            </div>
                                        </div>

                                        {item.title && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Publication Title</span>
                                                <span style={{ fontSize: '0.875rem', color: '#1f2937', fontStyle: 'italic' }}>{item.title}</span>
                                            </div>
                                        )}

                                        {item.notes && (
                                            <div style={{ marginTop: '0.75rem' }}>
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Notes</span>
                                                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{item.notes}</span>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RSIDTABLE;